import React, { useState, useEffect } from "react";
import { Dialog } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import { getNestedCategories } from "../../Api/Api";
import { AnimatePresence, motion } from "framer-motion";

import door from "../../Assets/kmc_20211002_100242.jpg";
import fridge from "../../Assets/kmc_20210929_144539.jpg";
import ac from "../../Assets/ogac.jpg";

const subcategoryColors = [
  "bg-[#fff4e5] border-[#f4d3a4]",
  "bg-[#eaf7f3] border-[#b9e1d4]",
  "bg-[#eef2ff] border-[#cbd5ff]",
  "bg-[#fff0f2] border-[#f3c4ce]",
];

const Home = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      setError(false);
      try {
        const data = await getNestedCategories();
        setServices(data);
      } catch (err) {
        console.error("Failed to fetch service categories", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const handleSubcategoryClick = (subcategory) => {
    navigate(`/service/${subcategory.id}`);
  };

  return (
    <div className="relative overflow-hidden bg-white">
      {/* Ambient background watermark */}
      <div className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#0D004C]/[0.04] blur-3xl" />
      <div className="pointer-events-none absolute top-1/3 -left-32 w-[380px] h-[380px] rounded-full bg-[#FFB800]/[0.05] blur-3xl" />

      <div className="relative flex flex-col md:flex-row items-start justify-between gap-12 px-6 md:px-16 py-24 md:py-28">
        {/* LEFT — Service Discovery */}
        <div className="w-full md:w-1/2 md:pr-6">
          <span className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.18em] text-[#B8860B] uppercase mb-3">
            <span className="w-6 h-[2px] bg-[#FFB800] rounded-full" />
            Trusted Home Services
          </span>

          <h2 className="text-3xl md:text-[2.1rem] font-extrabold text-[#0D004C] leading-tight mb-2">
            What are you{" "}
            <span className="relative inline-block">
              looking for?
              <svg
                className="absolute left-0 -bottom-1 w-full"
                height="6"
                viewBox="0 0 120 6"
                preserveAspectRatio="none"
              >
                <path d="M0,4 Q60,0 120,4" stroke="#FFB800" strokeWidth="3" fill="none" strokeLinecap="round" />
              </svg>
            </span>
          </h2>
          <p className="text-sm text-gray-500 mb-8 max-w-sm">
            Pick a category and we'll match you with a verified genie near you.
          </p>

          {/* Loading skeleton */}
          {loading && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="flex flex-col items-center justify-center bg-gray-50 rounded-2xl p-5 h-[120px] animate-pulse"
                >
                  <div className="w-10 h-10 rounded-full bg-gray-200 mb-3" />
                  <div className="w-14 h-2.5 rounded-full bg-gray-200" />
                </div>
              ))}
            </div>
          )}

          {/* Error state */}
          {!loading && error && (
            <div className="bg-red-50 border border-red-100 rounded-2xl p-5 text-sm text-red-600 font-medium">
              Couldn't load service categories. Please refresh and try again.
            </div>
          )}

          {/* Empty state */}
          {!loading && !error && services.length === 0 && (
            <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6 text-sm text-gray-500">
              No categories available right now — check back shortly.
            </div>
          )}

          {/* Category grid */}
          {!loading && !error && services.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {services.map((service, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedService(service)}
                  className="group flex flex-col items-center justify-center bg-white rounded-2xl p-5 border border-gray-100 shadow-[0_1px_3px_rgba(13,0,76,0.06)] hover:shadow-[0_8px_24px_rgba(13,0,76,0.12)] hover:-translate-y-0.5 hover:border-[#0D004C]/15 transition-all duration-200"
                >
                  <div className="w-14 h-14 rounded-full bg-[#0D004C]/[0.06] group-hover:bg-[#FFB800]/15 flex items-center justify-center mb-3 transition-colors duration-200">
                    <img
                      src={service.image}
                      alt={service.name}
                      className="w-8 h-8 object-contain"
                    />
                  </div>
                  <p className="text-center text-[13px] font-semibold text-[#0D004C] leading-snug">
                    {service.name}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT — Image Collage with floating trust badge */}
        <div className="hidden md:block relative w-1/2">
          <div className="grid grid-cols-2 gap-3 h-[440px]">
            <img
              src={door}
              alt="Professional home service"
              className="row-span-2 w-full h-full object-cover rounded-3xl shadow-lg"
            />
            <img
              src={fridge}
              alt="Appliance repair service"
              className="w-full h-full object-cover rounded-3xl shadow-lg"
            />
            <img
              src={ac}
              alt="AC service"
              className="w-full h-full object-cover rounded-3xl shadow-lg"
            />
          </div>

          {/* Floating credential card */}
          <div className="absolute -bottom-5 left-6 bg-white rounded-2xl shadow-xl px-5 py-3.5 flex items-center gap-3 border border-gray-100">
            <div className="w-9 h-9 rounded-full bg-[#0D004C] flex items-center justify-center text-[#FFB800] text-sm font-black">
              ★
            </div>
            <div>
              <p className="text-sm font-extrabold text-[#0D004C] leading-none">4.8 / 5</p>
              <p className="text-[11px] text-gray-500 mt-1">50,000+ homes served</p>
            </div>
          </div>
        </div>
      </div>

      {/* SUBCATEGORY MODAL */}
      <AnimatePresence>
        {selectedService && (
          <Dialog
            open={true}
            onClose={() => setSelectedService(null)}
            className="relative z-50"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-[#0D004C]/40 backdrop-blur-sm"
              aria-hidden="true"
            />

            <div className="fixed inset-0 flex items-end justify-center sm:items-center sm:p-4">
              <Dialog.Panel
                as={motion.div}
                key="modal"
                initial={{ opacity: 0, scale: 0.94, y: 30 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.94, y: 30 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="relative max-h-[54vh] w-full overflow-y-auto rounded-t-[2rem] bg-white p-6 pt-8 shadow-2xl sm:max-h-[80vh] sm:max-w-xl sm:rounded-3xl sm:p-7 sm:pt-10"
              >
                <div className="mx-auto mb-6 h-1 w-12 rounded-full bg-gray-200 sm:hidden" />
                {/* Close button */}
                <button
                  onClick={() => setSelectedService(null)}
                  aria-label="Close"
                  className="absolute right-5 top-5 flex h-8 w-8 items-center justify-center rounded-full bg-gray-50 text-gray-500 transition hover:bg-gray-100 hover:text-[#0D004C]"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                <Dialog.Title className="mb-1 text-xl font-extrabold text-[#0D004C]">
                  {selectedService?.name}
                </Dialog.Title>
                <p className="mb-5 text-xs leading-relaxed text-gray-400">
                  Select a service to view pricing and availability.
                </p>

                {selectedService?.subcategories?.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {selectedService.subcategories.map((sub, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSubcategoryClick(sub)}
                        className={`group flex min-h-[8.5rem] flex-col items-center justify-center rounded-2xl border p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-[#0D004C] hover:bg-[#0D004C] ${subcategoryColors[idx % subcategoryColors.length]}`}
                      >
                        <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-sm ring-4 ring-white/60 transition-transform duration-200 group-hover:scale-105">
                          <img
                            src={sub.image}
                            alt={sub.name}
                            className="h-10 w-10 object-contain"
                          />
                        </div>
                        <p className="text-[12px] text-center font-semibold text-gray-700 group-hover:text-white transition-colors duration-200">
                          {sub.name}
                        </p>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-2xl p-5 text-sm text-gray-500">
                    Details or booking options for{" "}
                    <strong className="text-[#0D004C]">{selectedService?.name}</strong> will
                    appear here.
                  </div>
                )}
              </Dialog.Panel>
            </div>
          </Dialog>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Home;